import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    FaClock, FaUserCog, FaFileAlt, FaHeadset, FaBug,
    FaFileDownload, FaChartLine,
    FaExternalLinkAlt, FaLock, FaShieldAlt, FaArrowRight, FaArrowUp, FaTools, FaWhatsapp,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { formatDate } from "./utils/time";
import { useScrollReveal, spotlight } from "./utils/useScrollReveal";
import { useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import BannerCarousel from "./components/BannerCarousel";
import { SectionHeader, EmptyState, CardGridSkeleton } from "./components/ui";
import { useContentModal } from "./components/ContentModal";
import "./Home.css";

const APPS = [
    // `roles` (optional) restricts a tile to those access levels. An app WITHOUT roles is
    // shown to everyone (visitors see a locked tile); an app WITH roles is shown only to a
    // signed-in employee whose role is listed — never to a visitor. This is launcher UX,
    // not a security boundary: each target app enforces its own access on its own domain.
    { name: "dAdmin", description: "Admin controls & configuration", url: "https://dadmin.dolluzcorp.com/Support_Tickets", icon: <FaUserCog />, roles: ["Admin", "Sub Admin"] },
    { name: "dForms", description: "Dynamic form builder system", url: "https://dforms.dolluzcorp.com/home", icon: <FaFileAlt /> },
    { name: "dAssist", description: "Support tickets and helpdesk", url: "https://dassist.dolluzcorp.com/Tickets", icon: <FaHeadset /> },
    { name: "dBug", description: "Bug tracking and issue management", url: "https://dbug.dolluzcorp.com/Tickets", icon: <FaBug /> },
    { name: "dTime", description: "Manage attendance, leave, shifts", url: "https://dtime.dolluzcorp.com/Timesheet_entry", icon: <FaClock /> },
    { name: "dSlip", description: "Payslips, Form 16 & health cards", url: "https://dslip.dolluzcorp.com/", icon: <FaFileDownload /> },
    { name: "dEpr", description: "Employee performance reviews", url: "https://depr.dolluzcorp.com/", icon: <FaChartLine />, roles: ["Admin"] },
];

const CERTS = [
    { src: "https://www.dolluzcorp.com/wp-content/themes/dolluzcorp/images/hippa.png", label: "HIPAA" },
    { src: "https://www.dolluzcorp.com/wp-content/themes/dolluzcorp/images/9001.png", label: "ISO 9001:2015" },
    { src: "https://www.dolluzcorp.com/wp-content/themes/dolluzcorp/images/27001.png", label: "ISO 27001:2013" },
];

const TEASER_COUNT = 3;

/**
 * dAdmin owns a per-app visibility mode (visitors | employees | hidden | maintenance),
 * served from a public, credential-less GET. It layers ON TOP OF the role logic below —
 * it never replaces canSeeApp or the roles. If dAdmin is unreachable, every app falls
 * back to "employees" (today's behavior), so an outage can't break the launcher.
 */
const DADMIN_API = process.env.NODE_ENV === "production"
    ? "https://dadmin.dolluzcorp.com"
    : "http://localhost:4002";

/** Varied floating-island shapes, cycled across however many apps this viewer sees. */
const ISLE = ["isle-a", "isle-b", "isle-c", "isle-d", "isle-e"];

/** A little icon variety across the expanded policy cards. Purely decorative. */
const POLICY_ICONS = [<FaShieldAlt />, <FaLock />, <FaClock />, <FaFileAlt />];

/** Floating WhatsApp contact — opens a chat to this number with the message pre-filled. */
const WA_NUMBER = "917779079666";
const WA_TEXT = "Hello, I have a question about your service. Can you please help me?";
const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_TEXT)}`;

/**
 * PAGE ORDER: header → hero (glass stage) → apps (islands) → updates
 *             (feature + timeline) → blog (editorial) → policies (panel) → certs → footer.
 *
 * Motion is CSS + one IntersectionObserver (useScrollReveal) — no animation library.
 * Everything degrades to "just visible" under prefers-reduced-motion.
 */
const Home = () => {
    const { isAuthenticated, isSectionVisible, sections, employee } = useAuth();
    const { openContent } = useContentModal();   // updates & policies open in a modal

    /**
     * Which dApp tiles this viewer may see. A role-restricted app (e.g. dAdmin, dEpr) is
     * hidden from visitors entirely and shown only to a signed-in employee whose access
     * level is in the app's `roles`. Matching is case-insensitive on the role name.
     */
    const viewerRole = (employee?.emp_access_level || "").trim().toLowerCase();
    const canSeeApp = (app) => {
        if (!app.roles) return true;              // unrestricted → visible to all (visitor gets a locked tile)
        if (!isAuthenticated) return false;       // restricted → never shown to a visitor
        return app.roles.some((r) => r.toLowerCase() === viewerRole);
    };

    /**
     * dAdmin's per-app visibility modes, fetched once on mount — SEPARATE from the content
     * loads, and non-blocking: a dAdmin outage leaves appModes empty and every app falls
     * back to its default (below), so the launcher never breaks.
     */
    const [appModes, setAppModes] = useState({});
    useEffect(() => {
        fetch(`${DADMIN_API}/api/app-visibility`)
            .then((r) => r.json())
            .then((d) => setAppModes(d?.apps || {}))
            .catch(() => setAppModes({}));   // dAdmin outage must NOT break the launcher
    }, []);
    /**
     * Each app carries TWO independent fields from dAdmin: `mode` (visibility) and
     * `maintenance` (an overlay that can be true with ANY mode). Fallback matches dAdmin's
     * own defaults — a public app is open to visitors, a role-gated app is employees-only,
     * and nothing is in maintenance unless dAdmin says so.
     */
    const stateOf = (app) =>
        appModes[app.name] || { mode: app.roles ? "employees" : "visitors", maintenance: false };

    /**
     * Which tiles are visible — decided by MODE ONLY (maintenance never changes
     * visibility), layered on top of the role floor:
     *   - "hidden"              → hidden for EVERYONE, including Admins.
     *   - fails canSeeApp       → hidden (role floor: a visitor never sees dAdmin/dEpr).
     *   - visitor + "employees" → hidden (employee-only tiles aren't shown to visitors).
     *   - otherwise             → visible, as a real launch.
     * Every visible tile is a genuine launch now — there is no "log in to launch" locked
     * state any more; a visitor either sees an open tile or doesn't see it at all.
     */
    const visibleApps = APPS.filter((app) => {
        const { mode } = stateOf(app);
        if (mode === "hidden") return false;
        if (!canSeeApp(app)) return false;
        if (!isAuthenticated && mode === "employees") return false;   // employee-only: hidden from visitors
        return true;
    });

    const navigate = useNavigate();
    const location = useLocation();

    const [updates, setUpdates] = useState(null);   // null = loading
    const [policies, setPolicies] = useState(null);
    const [blogs, setBlogs] = useState(null);
    const [showTop, setShowTop] = useState(false);

    // The policies panel expands inline into individual policy cards, rather than
    // navigating away — the drawer holds the real policy rows this viewer can see.
    const [policiesOpen, setPoliciesOpen] = useState(false);
    const togglePolicies = () => setPoliciesOpen((o) => !o);
    const policyKey = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePolicies(); }
    };

    // How many banners this viewer sees, reported up by the carousel. null = still
    // loading. When it settles on 0, the hero copy takes the full width instead of
    // leaving an empty glass stage.
    const [bannerCount, setBannerCount] = useState(null);
    const heroSolo = bannerCount === 0;

    const heroRef = useRef(null);

    /**
     * Refetched when auth changes: the SAME endpoints return more rows once you are
     * logged in, because the server widens the audience filter. Logging in fills the
     * page in — it does not navigate you anywhere else.
     */
    useEffect(() => {
        const load = async (endpoint, setter) => {
            try {
                const res = await apiFetch(endpoint);
                const data = await res.json();
                setter(data.success ? data.data : []);
            } catch (err) {
                console.error(`Failed to load ${endpoint}`, err);
                setter([]);
            }
        };

        load("/api/Dolluzcorp/updates/list", setUpdates);
        load("/api/Dolluzcorp/policies/list", setPolicies);
        load("/api/Dolluzcorp/blog/posts", setBlogs);
    }, [isAuthenticated]);

    /**
     * Re-scan for .reveal nodes whenever content arrives, so cards animate in too.
     * `sections` MUST be in this list — a teaser section only mounts once section
     * visibility resolves, usually AFTER the content fetch; without it those late cards
     * would sit at opacity:0 forever.
     */
    useScrollReveal([updates, policies, blogs, sections, policiesOpen]);

    // Show the back-to-top button once the hero is behind you.
    useEffect(() => {
        let frame = 0;
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                setShowTop(window.scrollY > window.innerHeight * 0.9);
                frame = 0;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    /** The hero aurora drifts toward the cursor, and the glass stage tilts with it —
     *  depth without a parallax library. Written as --mx/--my, which descendants inherit. */
    const heroParallax = (e) => {
        const el = heroRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width - 0.5) * 2}`);
        el.style.setProperty("--my", `${((e.clientY - r.top) / r.height - 0.5) * 2}`);
    };

    /**
     * Arriving from another page via the header nav: it can't scroll to a section that
     * isn't mounted yet, so it hands the target over in location.state and we do it here
     * on the next frame, once this page has painted.
     */
    useEffect(() => {
        const target = location.state?.scrollTo;
        if (!target) return;

        const frame = requestAnimationFrame(() => {
            if (target === "top") window.scrollTo({ top: 0 });
            else document.getElementById(target)?.scrollIntoView({ block: "start" });
            navigate(".", { replace: true, state: null });
        });
        return () => cancelAnimationFrame(frame);
    }, [location.state, navigate]);

    const teaserUpdates = updates?.slice(0, TEASER_COUNT) ?? [];
    const featureUpdate = teaserUpdates[0];
    const timelineUpdates = teaserUpdates.slice(1);

    return (
        <>
            <a className="skip-link" href="#main">Skip to content</a>
            <Header />

            <main id="main">
                {/* ── HERO ───────────────────────────────────────────────────
                    Copy on the LEFT, the banner carousel presented inside a tilted
                    glass stage on the RIGHT. When the viewer has zero banners the copy
                    takes the full width and the stage is dropped (see `heroSolo`). */}
                <section className="hero" aria-labelledby="hero-h" ref={heroRef} onMouseMove={heroParallax}>
                    <div className="atmos" aria-hidden="true">
                        <span className="au au-1" />
                        <span className="au au-2" />
                        <span className="au au-3" />
                        <span className="dots" />
                        <span className="grain" />
                    </div>

                    <div className={`page-wrap hero-inner${heroSolo ? " solo" : ""}`}>
                        <div className="hero-copy">
                            <span className="hero-badge reveal">
                                <i aria-hidden="true" /> Inside Dolluz Corp
                            </span>

                            {/* The real whitespace after "the" matters: the <em> is a
                                block visually, but the heading's accessible name is its
                                textContent — without the space it reads "theModern". */}
                            <h1 id="hero-h" className="hero-title reveal" style={{ "--d": "80ms" }}>
                                Empowering the{" "}
                                <em className="hero-shine">Modern Workforce</em>
                            </h1>

                            <p className="hero-lead reveal" style={{ "--d": "160ms" }}>
                                Dolluz Corp builds an integrated ecosystem of enterprise applications that
                                streamline how teams work - from HR and support to development and beyond.
                                This is where we share what we're building and what's new.
                            </p>

                            {/* Each CTA points at a section, so it follows that section's
                                visibility — otherwise the hero keeps offering a button that
                                404s and reveals a section this viewer shouldn't know about. */}
                            {(isSectionVisible("blog") || isSectionVisible("updates")) && (
                                <div className="hero-actions reveal" style={{ "--d": "240ms" }}>
                                    {isSectionVisible("blog") && (
                                        <Link to="/blog" className="btn btn-primary btn-lg btn-shine">
                                            Read our stories <FaArrowRight aria-hidden="true" />
                                        </Link>
                                    )}
                                    {isSectionVisible("updates") && (
                                        <Link
                                            to="/updates"
                                            className={`btn btn-lg ${isSectionVisible("blog") ? "btn-secondary" : "btn-primary btn-shine"}`}
                                        >
                                            Latest updates
                                        </Link>
                                    )}
                                </div>
                            )}

                            <div className="hero-facts reveal" style={{ "--d": "320ms" }}>
                                <span><b>{APPS.length}</b> apps in the suite</span><s>/</s>
                                <span><b>1</b> sign-in for all of them</span><s>/</s>
                                <span><b>3</b> audited standards</span>
                            </div>

                            <div className="hero-cue" aria-hidden="true">Scroll</div>
                        </div>

                        {/* The banner column, presented as the signature tilted glass stage.
                            Kept mounted even when hidden (solo), so it re-fetches on login and
                            can bring the stage back if the viewer's audience gains a banner. */}
                        <div className="stage reveal" style={{ "--d": "200ms" }}>
                            <span className="stage-glow" aria-hidden="true" />
                            <div className="stage-inner">
                                {/* Decorative brand chips floating on the banner's corners. */}
                                <span className="stage-chip chip-a" aria-hidden="true"><i /> One login · every app</span>
                                <span className="stage-chip chip-b" aria-hidden="true"><i /> ISO 27001 certified</span>
                                <div className="stage-frame">
                                    <BannerCarousel isAuthenticated={isAuthenticated} onCountChange={setBannerCount} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── THE SUITE · floating application islands ─────────────────
                    Every visible tile is a REAL launch now — dAdmin's per-app modes decide
                    which tiles appear (a visitor sees only the apps opened to visitors), so
                    there is no "log in to launch" locked state. If nothing is visible for
                    this viewer, the whole section is dropped. */}
                {visibleApps.length > 0 && (
                <section id="apps" className="section isles-sec" aria-labelledby="apps-h">
                    <span className="hairtop" aria-hidden="true" />
                    <div className="page-wrap">
                        <SectionHeader
                            eyebrow="Applications"
                            title={<span id="apps-h">Our dApps</span>}
                            subtitle={
                                isAuthenticated
                                    ? "Jump straight into the tools you use every day."
                                    : "The suite of applications we build and run in-house - jump straight in."
                            }
                        />

                        <div className="isles">
                            {visibleApps.map((app, i) => {
                                const maintenance = stateOf(app).maintenance === true;
                                return (
                                    <div
                                        key={app.name}
                                        className={`app-card reveal ${ISLE[i % ISLE.length]}`}
                                        style={{ "--d": `${i * 70}ms` }}
                                        onMouseMove={spotlight}
                                    >
                                        <span className="app-icon" aria-hidden="true">{app.icon}</span>
                                        <div className="app-body">
                                            <h3 className="app-name">{app.name}</h3>
                                            <p className="app-desc">{app.description}</p>

                                            {/* Maintenance is a label only — the link still
                                                points at the app, whose own nginx serves the
                                                maintenance page. Kept IN FLOW (not absolute) so
                                                it never overlaps the Launch button on the wide
                                                row-layout islands. */}
                                            {maintenance && (
                                                <span className="chip" style={{ marginTop: "10px" }}>
                                                    <FaTools aria-hidden="true" /> Maintenance
                                                </span>
                                            )}
                                        </div>

                                        <a
                                            className="btn btn-secondary btn-sm btn-block btn-shine"
                                            href={app.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={
                                                maintenance
                                                    ? `Launch ${app.name} (under maintenance) - ${app.description}`
                                                    : `Launch ${app.name} - ${app.description}`
                                            }
                                        >
                                            Launch <FaExternalLinkAlt aria-hidden="true" />
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
                )}

                {/* ── UPDATES · feature + timeline ────────────────────────────── */}
                {isSectionVisible("updates") && (
                <section id="updates" className="section" aria-labelledby="updates-h">
                    <div className="page-wrap">
                        <SectionHeader
                            eyebrow="Latest"
                            title={<span id="updates-h">Updates &amp; Announcements</span>}
                            subtitle="Everything that changed across the suite and the office, newest first."
                            action={
                                updates?.length > TEASER_COUNT && (
                                    <Link to="/updates" className="link-cta">
                                        View all <span className="card-arrow">→</span>
                                    </Link>
                                )
                            }
                        />

                        {updates === null ? (
                            <CardGridSkeleton count={3} className="teaser-grid" />
                        ) : updates.length === 0 ? (
                            <EmptyState
                                icon="📣"
                                title="No announcements right now"
                                message="There's nothing published at the moment. New announcements will show up here."
                            />
                        ) : (
                            <div className="updates-layout">
                                <article className="update-feature reveal">
                                    <button
                                        type="button"
                                        className="card-cover"
                                        onClick={() => openContent("update", featureUpdate.update_id)}
                                        aria-label={featureUpdate.title}
                                    />
                                    <div className="update-feature-body">
                                        <div className="update-tags"><span className="chip">Update</span></div>
                                        <h3 className="update-feature-title">{featureUpdate.title}</h3>
                                        {featureUpdate.subject && (
                                            <p className="update-feature-teaser">{featureUpdate.subject}</p>
                                        )}
                                        <div className="update-feature-foot">
                                            <span className="date-chip">
                                                {formatDate(featureUpdate.updated_time || featureUpdate.created_time)}
                                            </span>
                                            <span className="link-cta">Read more <span className="card-arrow">→</span></span>
                                        </div>
                                    </div>
                                    <div className="update-feature-visual" aria-hidden="true">
                                        <span className="uf-badge">Latest<br />update</span>
                                    </div>
                                </article>

                                {timelineUpdates.length > 0 && (
                                    <ul className="update-timeline">
                                        {timelineUpdates.map((u, i) => (
                                            <li key={u.update_id} className="update-item reveal" style={{ "--d": `${90 + i * 90}ms` }}>
                                                <span className="update-dot" aria-hidden="true" />
                                                <button
                                                    type="button"
                                                    className="card-cover"
                                                    onClick={() => openContent("update", u.update_id)}
                                                    aria-label={u.title}
                                                />
                                                <div className="update-tags"><span className="chip">Update</span></div>
                                                <h4 className="update-item-title">{u.title}</h4>
                                                {u.subject && <p className="update-item-teaser">{u.subject}</p>}
                                                <span className="date-chip">{formatDate(u.updated_time || u.created_time)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </section>
                )}

                {/* ── BLOG · angled editorial cards ───────────────────────────── */}
                {isSectionVisible("blog") && (
                <section id="blog" className="section section-soft" aria-labelledby="blog-h">
                    <div className="page-wrap">
                        <SectionHeader
                            eyebrow="Knowledge"
                            title={<span id="blog-h">From the Blog</span>}
                            subtitle="Engineering notes, architecture decisions and product stories from the team."
                            action={
                                blogs?.length > TEASER_COUNT && (
                                    <Link to="/blog" className="link-cta">
                                        All posts <span className="card-arrow">→</span>
                                    </Link>
                                )
                            }
                        />

                        {blogs === null ? (
                            <CardGridSkeleton count={3} className="teaser-grid" />
                        ) : blogs.length === 0 ? (
                            <EmptyState
                                icon="📝"
                                title="No posts published yet"
                                message="Stories and engineering write-ups from the team will appear here."
                            />
                        ) : (
                            <div className="editorial">
                                {blogs.slice(0, TEASER_COUNT).map((b, i) => (
                                    <article key={b.id} className={`ed ed-${i + 1} reveal`} style={{ "--d": `${i * 90}ms` }}>
                                        <Link to={`/blog/${b.id}`} className="card-cover" aria-label={b.title} />
                                        <span className="chip chip-blog">{b.category_name || "Blog"}</span>
                                        <h3>{b.title}</h3>
                                        {b.excerpt && <p>{b.excerpt}</p>}
                                        <div className="ed-foot">
                                            <span className="mono">{formatDate(b.published_at)}</span>
                                            <span className="ed-read" aria-hidden="true"><FaArrowRight /></span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
                )}

                {/* ── POLICIES · panel expands into individual policy cards ─────
                    Clicking the panel opens a drawer of the real policies this viewer
                    can see; each card links to its full detail page. */}
                {isSectionVisible("policies") && (
                <section id="policies" className="section policy-sec" aria-labelledby="policies-h">
                    <div className="page-wrap">
                        <div
                            className={`policy-panel reveal${policiesOpen ? " is-open" : ""}`}
                            role={policies?.length > 0 ? "button" : undefined}
                            tabIndex={policies?.length > 0 ? 0 : undefined}
                            aria-expanded={policies?.length > 0 ? policiesOpen : undefined}
                            aria-controls={policies?.length > 0 ? "policy-drawer" : undefined}
                            onClick={policies?.length > 0 ? togglePolicies : undefined}
                            onKeyDown={policies?.length > 0 ? policyKey : undefined}
                            onMouseMove={spotlight}
                        >
                            <span className="policy-fx" aria-hidden="true" />
                            <span className="policy-glyph" aria-hidden="true"><FaShieldAlt /></span>

                            <div className="policy-body">
                                <span className="eyebrow">Compliance</span>
                                <h2 id="policies-h" className="section-title">Policies &amp; Guidelines</h2>
                                <p>
                                    {policies === null
                                        ? "Loading…"
                                        : policies.length === 0
                                            ? "Nothing published here yet. Policies will appear as they're added."
                                            : `${policies.length} ${policies.length === 1 ? "policy" : "policies"} covering conduct, security and ways of working.`}
                                </p>

                                {policies?.length > 0 && (
                                    <>
                                        <div className="policy-list">
                                            {policies.slice(0, 3).map((p) => (
                                                <span key={p.policy_id}>{p.title}</span>
                                            ))}
                                        </div>
                                        <span className="policy-hint">
                                            {policiesOpen ? "Tap to collapse" : "Tap to view all policies"}
                                        </span>
                                    </>
                                )}
                            </div>

                            {policies?.length > 0 && (
                                <span className="policy-go" aria-hidden="true"><FaArrowRight /></span>
                            )}
                        </div>

                        {policies?.length > 0 && (
                            <div className={`policy-drawer${policiesOpen ? " open" : ""}`} id="policy-drawer">
                                <div className="policy-grid">
                                    {policies.map((p, i) => (
                                        <button
                                            key={p.policy_id}
                                            type="button"
                                            className="policy-card reveal"
                                            style={{ "--d": `${i * 70}ms` }}
                                            tabIndex={policiesOpen ? 0 : -1}
                                            onClick={() => openContent("policy", p.policy_id)}
                                        >
                                            <span className="policy-card-icon" aria-hidden="true">
                                                {POLICY_ICONS[i % POLICY_ICONS.length]}
                                            </span>
                                            <h3 className="policy-card-title">{p.title}</h3>
                                            {p.excerpt && <p className="policy-card-desc">{p.excerpt}</p>}
                                            <span className="policy-card-arrow" aria-hidden="true"><FaArrowRight /></span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
                )}

                {/* ── CERTIFICATIONS · dark showcase ──────────────────────────── */}
                <section className="certs" aria-labelledby="certs-h">
                    <span className="certs-fx" aria-hidden="true" />
                    <span className="certs-beam" aria-hidden="true" />

                    <div className="page-wrap">
                        <div className="certs-head reveal">
                            <span className="eyebrow">Recognition</span>
                            <h2 id="certs-h">Certified &amp; Compliant</h2>
                            <p>
                                We hire detail-oriented people and hold ourselves to audited standards,
                                so our clients get the best assistance and experience.
                            </p>
                        </div>

                        <ul className="certs-row">
                            {CERTS.map((c, i) => (
                                <li key={c.label} className="cert reveal" style={{ "--d": `${i * 110}ms` }}>
                                    <img src={c.src} alt={`${c.label} certification`} loading="lazy" />
                                    <span className="cert-name">{c.label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </main>

            <Footer />

            {/* Floating WhatsApp contact. Sits at the bottom-right corner, and rises to
                sit ABOVE the back-to-top arrow once that appears on scroll. */}
            <a
                className={`wa-fab${showTop ? " raised" : ""}`}
                href={WA_HREF}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with us on WhatsApp"
                title="Chat with us on WhatsApp"
            >
                <FaWhatsapp aria-hidden="true" />
            </a>

            <button
                type="button"
                className={`to-top${showTop ? " show" : ""}`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                aria-label="Back to top"
            >
                <FaArrowUp aria-hidden="true" />
            </button>
        </>
    );
};

export default Home;
